import DeleteIcon from '@mui/icons-material/Delete';
import { Grid, IconButton } from '@mui/material';
import { useFormikContext } from 'formik';
import { Dispatch, memo, SetStateAction, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';

import {
    GetGetResourcesByTypeResponse,
    KeyGetGetResourcesByType,
    ResourceType,
    ResourceWebServicesApiDeleteResourceRequest,
    useMutationDeleteDeleteResource,
} from '../../../../../../backend';
import { queryClient } from '../../../../../../Root';
import { errorToastConfig } from '../../../../../../utils/utils';
import { useConfirmDialog } from '../../../../../confirm/useConfirmDialog';
import { ResourcesPageFormValues } from '../../../../../pages/ResourcesPage';
import { StyledLineContainerBox } from './LineContainer.style';
import { ComponentByType } from './LineContainer.utils';
import { ResourceFieldsLineCommonProperties } from './ResourceFieldsLineCommonProperties';

export const LineContainer = memo(function LineContainer({
    resourceType,
    index,
    arrayReplace,
    arrayRemove,
    selectedResources,
    setSelectedResources,
}: {
    resourceType: ResourceType;
    index: number;
    arrayReplace: (index: number, value: any) => void;
    arrayRemove: (index: number) => void;
    selectedResources: number[];
    setSelectedResources: Dispatch<SetStateAction<number[]>>;
}) {
    const Component = ComponentByType[resourceType];
    const { values, dirty } = useFormikContext<ResourcesPageFormValues>();
    const confirm = useConfirmDialog({ disableReject: true });

    const { mutate } = useMutationDeleteDeleteResource({
        onError: (e, args: ResourceWebServicesApiDeleteResourceRequest) => {
            toast(
                <FormattedMessage
                    id={'resourcesForm.error.deleteResource'}
                    values={{ type: args.type, name: args.name }}
                />,
                errorToastConfig,
            );
        },
        onSuccess: (r, requestArgs) => {
            queryClient.setQueriesData(
                [
                    KeyGetGetResourcesByType,
                    {
                        type: resourceType,
                        includeNonActive: true,
                    },
                ],
                (values: GetGetResourcesByTypeResponse) => ({
                    ...values,
                    data: values.data.filter(r => r.name !== requestArgs.name),
                }),
            );
        },
    });

    const handleDelete = useCallback(
        (type: ResourceType, name: string, id: string | null) => {
            confirm({
                title: (
                    <FormattedMessage
                        id={'resources.actions.delete'}
                        values={{ name: name, type: <FormattedMessage id={'form.value.type.' + type} /> }}
                    />
                ),
                content:
                    values.resources.find(r => r.name === name)?.id && dirty ? (
                        <FormattedMessage id={'resources.alert.delete'} />
                    ) : null,
                confirmationText: <FormattedMessage id={'confirm.default.delete'} />,
                cancellationText: <FormattedMessage id={'confirm.default.cancel'} />,
            }).then(() => {
                if (id) {
                    mutate({ type: type, name: name });
                }
                arrayRemove(index);
                setSelectedResources(selectedResources.filter(i => i !== index));
            });
        },
        [arrayRemove, confirm, dirty, index, mutate, selectedResources, setSelectedResources, values.resources],
    );

    return (
        <StyledLineContainerBox selected={selectedResources.includes(index)} existing={!!values.resources[index].id}>
            <Grid container>
                <ResourceFieldsLineCommonProperties
                    index={index}
                    arrayReplace={arrayReplace}
                    selectedResources={selectedResources}
                    setSelectedResources={setSelectedResources}
                />
                {Component !== undefined ? <Component index={index} arrayReplace={arrayReplace} /> : null}
                <Grid width={'52px'} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                    <IconButton
                        aria-label="delete"
                        component="label"
                        onClick={() =>
                            handleDelete(resourceType, values.resources[index].name, values.resources[index].id)
                        }
                    >
                        <DeleteIcon />
                    </IconButton>
                </Grid>
            </Grid>
        </StyledLineContainerBox>
    );
});
